import { Box, ButtonBase, InputBase, useTheme } from "@mui/material";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useGetOptionsQuery } from "../../../../../newapi/pickingjob/pickingjobSlice";
import { useGetUsersSelectQuery } from "../../../../../newapi/user/userSlice";
import { useFindPickupCartSelectQuery } from "../../../../../newapi/warehouse/pickupCartSlice";
import { useGetProductsSelectQuery } from "../../../../../newapi/warehouse/productSlice";
import { useGetWarehousesSelectQuery } from "../../../../../newapi/warehouse/warehouseApiSlice";
import { tokens } from "../../../../../theme";
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";
import Shbutton from "../../../../theme/buttons/Shbutton";
import { ShDropdownQuery, ShDropdownSearchQuery } from "../../../../theme/dropdowns/Dropdown";
import ShTextSkeleton from "../../../../theme/skeleton/ShTextSkeleton";
import Heading from "../../../../theme/text/Heading";
import Subheading from "../../../../theme/text/Subheading";
import Text from "../../../../theme/text/Text";
import { CountShQueryDropdown } from "../components/CountShQueryDropdown";
import { CartDropdownComponent } from "../components/CartDropdownComponent";
import MConfirmation from "../../MConfirmation";
import { useMemo, useState } from "react";
import { getShipperColorSettingsByType } from "../../../../Streams/StreamShipperSettings";
import { getShipperLogo } from "../../../../../utils/logomapper";
import { ProfileRow } from "../components/ProfileRow";
import { MEditBatchOrders } from "../MEditBatchOrders";



const defaultColorSettings = {
    base: "#31415f",
    background: "#ecf0f5",
    streamrule: "#ced7e1",
};


export const CheckBatchBody = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const allOrders = props.job?.orders || [];
    const groupBy = props.groupBy || "shipper";
    const { changes, setChanges } = props;

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        orderIds: [],
        profileName: '',
    });

    const [editModal, setEditModal] = useState({
        open: false,
        orders: [],
        profileData: null,
    });

    const orders = useMemo(() => {
        return allOrders
            .filter(order => !changes.ordersToRemove.includes(order.id))
            .map(order => {
                const change = changes.orderChanges.find(c => c.id === order.id);
                if (change?.shipper && change?.shipperProfile) {
                    return {
                        ...order,
                        shipper: change.shipper,
                        shipperProfile: change.shipperProfile,
                    };
                }
                return order;
            });
    }, [allOrders, changes.ordersToRemove, changes.orderChanges]);

    const groupedByShipper = useMemo(() => {
        const shipperMap = new Map();

        orders.forEach(order => {
            if (!order.shipper) return;

            const shipperId = order.shipper.id;

            if (!shipperMap.has(shipperId)) {
                shipperMap.set(shipperId, {
                    shipper: order.shipper,
                    profiles: new Map()
                });
            }

            const shipperData = shipperMap.get(shipperId);
            const profileId = order.shipperProfile?.id || 'unknown';

            if (!shipperData.profiles.has(profileId)) {
                shipperData.profiles.set(profileId, {
                    profile: order.shipperProfile,
                    shipper: order.shipper,
                    orderIds: [],
                    count: 0
                });
            }

            const profileData = shipperData.profiles.get(profileId);
            profileData.orderIds.push(order.id);
            profileData.count++;
        });

        return Array.from(shipperMap.values())
            .map(shipperData => ({
                shipper: shipperData.shipper,
                profiles: Array.from(shipperData.profiles.values())
            }))
            .filter(group => group.profiles.length > 0);
    }, [orders]);

    const groupedByRelation = useMemo(() => {
        const relationMap = new Map();

        orders.forEach(order => {
            const relationName = order.relation?.name || null;
            const relationKey = relationName || '__no_relation__';

            if (!relationMap.has(relationKey)) {
                relationMap.set(relationKey, {
                    relationName: relationName,
                    profiles: new Map()
                });
            }

            const relationData = relationMap.get(relationKey);
            const profileId = order.shipperProfile?.id || 'unknown';
            const profileKey = `${order.shipper?.id || 'unknown'}_${profileId}`;

            if (!relationData.profiles.has(profileKey)) {
                relationData.profiles.set(profileKey, {
                    profile: order.shipperProfile,
                    shipper: order.shipper,
                    orderIds: [],
                    count: 0
                });
            }

            const profileData = relationData.profiles.get(profileKey);
            profileData.orderIds.push(order.id);
            profileData.count++;
        });

        return Array.from(relationMap.values())
            .map(relationData => ({
                relationName: relationData.relationName,
                profiles: Array.from(relationData.profiles.values())
            }))
            .filter(group => group.profiles.length > 0);
    }, [orders]);

    const handleDeleteClick = (orderIds, profileName) => {
        setConfirmModal({
            open: true,
            orderIds,
            profileName,
        });
    };

    const handleConfirmClose = () => {
        setConfirmModal({
            open: false,
            orderIds: [],
            profileName: '',
        });
    };

    const handleConfirmDelete = () => {
        setChanges(prev => ({
            ...prev,
            ordersToRemove: [...prev.ordersToRemove, ...confirmModal.orderIds],
        }));
        handleConfirmClose();
    };

    const handleEditClick = (profileData) => {
        const ordersForProfile = orders.filter(order =>
            profileData.orderIds.includes(order.id)
        );
        setEditModal({
            open: true,
            orders: ordersForProfile,
            profileData,
        });
    };

    const handleEditModalClose = () => {
        setEditModal({
            open: false,
            orders: [],
            profileData: null,
        });
    };

    const handleEditModalSave = (modalChanges) => {
        setChanges(prev => ({
            ...prev,
            ordersToRemove: [...prev.ordersToRemove, ...modalChanges.ordersToRemove],
            orderChanges: [
                ...prev.orderChanges.filter(c =>
                    !modalChanges.orderChanges.some(mc => mc.id === c.id)
                ),
                ...modalChanges.orderChanges,
            ],
        }));
    };

    return (
        <Box
            sx={{
                flex:1,
                display:"flex",
                flexDirection:'column',
                px:3,
                py:3,
                gap:2,
                overflow:'auto',
            }}
        >
            {groupBy === "shipper" && groupedByShipper.map((shipperGroup) => {
                const colorSettings = getShipperColorSettingsByType(shipperGroup.shipper.type);

                return (
                    <Box
                        key={shipperGroup.shipper.id}
                        sx={{
                            p:2,
                            background: colorSettings.background,
                            borderRadius:4,
                        }}
                    >
                        <Box
                            sx={{
                                display:"flex",
                                alignItems:'center',
                            }}
                        >
                            <Box
                                sx={{
                                    display:"inline-flex",
                                    alignItems:"center",
                                    gap:1,
                                    height:32,
                                    ml:"-8px",
                                    pl:"8px",
                                    pr:"8px",
                                }}
                            >
                                {getShipperLogo(shipperGroup.shipper.type)}
                            </Box>
                            <Box
                                sx={{
                                    ml:1,
                                    display:"flex",
                                    flexDirection:'column',
                                }}
                            >
                                <Text bold sx={{color:tokens("light").txt["primary"]}}>{shipperGroup.shipper.name}</Text>
                                <Text sx={{color:tokens("light").txt["secondary"]}}>{t("profiles_for_shipper_in_batch")}</Text>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                ml:6,
                                mt:2,
                                display:"flex",
                                flexDirection:'column',
                                gap:1,
                            }}
                        >
                            {shipperGroup.profiles.map((profileData) => (
                                <ProfileRow
                                    key={profileData.profile?.id || 'unknown'}
                                    profileData={profileData}
                                    colorSettings={colorSettings}
                                    onEdit={() => handleEditClick(profileData)}
                                    onDelete={() => handleDeleteClick(
                                        profileData.orderIds,
                                        profileData.profile?.name || t("unknown_profile")
                                    )}
                                />
                            ))}
                        </Box>
                    </Box>
                );
            })}

            {groupBy === "relation" && groupedByRelation.map((relationGroup, idx) => (
                <Box
                    key={relationGroup.relationName || `no-relation-${idx}`}
                    sx={{
                        p:2,
                        background: defaultColorSettings.background,
                        borderRadius:4,
                    }}
                >
                    <Box
                        sx={{
                            display:"flex",
                            alignItems:'center',
                        }}
                    >
                        <Box
                            sx={{
                                ml:1,
                                display:"flex",
                                flexDirection:'column',
                            }}
                        >
                            <Text bold>{relationGroup.relationName || t("no_relation")}</Text>
                            <Text light>{t("profiles_for_relation_in_batch")}</Text>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            ml:2,
                            mt:2,
                            display:"flex",
                            flexDirection:'column',
                            gap:1,
                        }}
                    >
                        {relationGroup.profiles.map((profileData) => {
                            const colorSettings = profileData.shipper
                                ? getShipperColorSettingsByType(profileData.shipper.type)
                                : defaultColorSettings;

                            return (
                                <ProfileRow
                                    key={`${profileData.shipper?.id || 'unknown'}_${profileData.profile?.id || 'unknown'}`}
                                    profileData={profileData}
                                    colorSettings={colorSettings}
                                    showShipperLogo={true}
                                    onEdit={() => handleEditClick(profileData)}
                                    onDelete={() => handleDeleteClick(
                                        profileData.orderIds,
                                        profileData.profile?.name || t("unknown_profile")
                                    )}
                                />
                            );
                        })}
                    </Box>
                </Box>
            ))}
            <MConfirmation
                open={confirmModal.open}
                handleClose={handleConfirmClose}
                data={{
                    title: t("remove_from_batch")
                }}
                confirm={handleConfirmDelete}
            />

            <MEditBatchOrders
                open={editModal.open}
                handleClose={handleEditModalClose}
                orders={editModal.orders}
                profileData={editModal.profileData}
                groupBy={groupBy}
                onSave={handleEditModalSave}
            />
        </Box>
    )
}

export const CheckBatchFooter = ({handleChange, isLoading, goBack}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    return (
        <>
            <Shbutton isCancel={true} className={"always-visible"} onClick={goBack} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
            <ButtonGroup version={2}>
                <Shbutton className={"group-btn"}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                isLoading={isLoading}
                onClick={() => handleChange()}
                >{t("create")}</Shbutton>
            </ButtonGroup>
        </>
    )
}
